import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import LogoImage from '../components/LogoImage';
import { useSimpleAuth } from '../contexts/SimpleAuthContext';

function BrandImage({ baseName, className }) {
  const safe = (name) => (name || '').toString();
  const raw = safe(baseName);
  const capFirst = raw.replace(/^(\w)/, (m) => m.toUpperCase());
  const titleCase = raw.split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  const dashed = safe(baseName).toLowerCase().replace(/\s+/g, '-');
  const underscored = safe(baseName).toLowerCase().replace(/\s+/g, '_');
  const ver = '8'; // cache-buster para forzar recarga de imágenes actualizadas
  const candidates = [
    // Nombres tal cual
    process.env.PUBLIC_URL + `/brand/${raw}.webp?v=${ver}`,
    process.env.PUBLIC_URL + `/brand/${raw}.png?v=${ver}`,
    process.env.PUBLIC_URL + `/brand/${raw}.jpg?v=${ver}`,
    process.env.PUBLIC_URL + `/brand/${raw}.jpeg?v=${ver}`,
    // Variantes capitalizadas
    process.env.PUBLIC_URL + `/brand/${capFirst}.webp?v=${ver}`,
    process.env.PUBLIC_URL + `/brand/${capFirst}.png?v=${ver}`,
    process.env.PUBLIC_URL + `/brand/${capFirst}.jpg?v=${ver}`,
    process.env.PUBLIC_URL + `/brand/${capFirst}.jpeg?v=${ver}`,
    process.env.PUBLIC_URL + `/brand/${titleCase}.webp?v=${ver}`,
    process.env.PUBLIC_URL + `/brand/${titleCase}.png?v=${ver}`,
    process.env.PUBLIC_URL + `/brand/${titleCase}.jpg?v=${ver}`,
    process.env.PUBLIC_URL + `/brand/${titleCase}.jpeg?v=${ver}`,
    // Variantes normalizadas
    process.env.PUBLIC_URL + `/brand/${dashed}.webp?v=${ver}`,
    process.env.PUBLIC_URL + `/brand/${dashed}.png?v=${ver}`,
    process.env.PUBLIC_URL + `/brand/${dashed}.jpg?v=${ver}`,
    process.env.PUBLIC_URL + `/brand/${dashed}.jpeg?v=${ver}`,
    process.env.PUBLIC_URL + `/brand/${underscored}.webp?v=${ver}`,
    process.env.PUBLIC_URL + `/brand/${underscored}.png?v=${ver}`,
    process.env.PUBLIC_URL + `/brand/${underscored}.jpg?v=${ver}`,
    process.env.PUBLIC_URL + `/brand/${underscored}.jpeg?v=${ver}`,
  ];
  const [index, setIndex] = React.useState(0);
  const onError = () => {
    if (index < candidates.length - 1) setIndex(index + 1);
  };
  return (
    <img
      src={candidates[index]}
      alt=""
      aria-hidden
      className={className}
      onError={onError}
      loading="lazy"
    />
  );
}

function isStandaloneMode() {
  try {
    const mq = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
    const iosStandalone = window.navigator && window.navigator.standalone;
    const twa = document.referrer && document.referrer.startsWith('android-app://');
    return Boolean(mq || iosStandalone || twa);
  } catch {
    return false;
  }
}

const Landing = () => {
  const navigate = useNavigate();
  const { user, userClaims, loading } = useSimpleAuth();
  const logoUrl = process.env.PUBLIC_URL + '/icons/icon-512.png?v=5';

  // Redirigir automáticamente en modo app (PWA/TWA) o si el usuario ya está autenticado
  useEffect(() => {
    if (isStandaloneMode()) {
      navigate('/login', { replace: true });
      return;
    }
    if (!loading && user) {
      // Usuario autenticado: respetar la lógica existente
      if (userClaims?.globalAdmin === true || userClaims?.role === 'globalAdmin') return navigate('/global-admin', { replace: true });
      if (userClaims?.isAdmin === true || userClaims?.role === 'businessAdmin') return navigate('/admin/day', { replace: true });
      if (userClaims?.role === 'client') return navigate('/booking', { replace: true });
      return navigate('/login', { replace: true });
    }
  }, [user, userClaims, loading, navigate]);

  return (
    <div className="bg-white min-h-screen">
      {/* Header simple con logo y CTA */}
      <header className="border-b border-gray-200 bg-white">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Logo anclado a la izquierda */}
          <div className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2">
            <LogoImage className="h-12 sm:h-14 w-auto" preferBrand />
          </div>
          {/* Título centrado y grande */}
          <h1 className="text-center font-extrabold text-gray-900 text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight">
            Reserberry
          </h1>
          {/* CTA anclado a la derecha */}
          <Link
            to="/login"
            className="absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 inline-flex items-center px-4 py-2 rounded-lg bg-rose-600 text-white font-medium hover:bg-rose-700 transition-colors"
          >
            Acceder
          </Link>
        </div>
      </header>

      {/* Hero con logo de fondo */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white to-rose-100">
        {/* Logo grande de fondo (oculto en pantallas estrechas para evitar solapes) */}
        <img
          src={logoUrl}
          alt=""
          aria-hidden
          className="pointer-events-none select-none opacity-100 absolute z-0 right-6 md:right-10 top-0 md:top-2 w-56 md:w-80 lg:w-[520px] h-auto hidden xl:block"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight">
              Gestiona tus citas y tu negocio con una app rápida y sencilla
            </h1>
            <p className="mt-4 text-lg text-gray-600">
              Planifica horarios, confirma reservas y envía recordatorios automáticos. Todo en una interfaz moderna,
              segura y lista para móvil y tablet.
            </p>
            <div className="mt-8 flex gap-3">
              <Link
                to="/login"
                className="inline-flex items-center px-5 py-3 rounded-lg bg-rose-600 text-white font-semibold hover:bg-rose-700 transition-colors"
              >
                Acceder
              </Link>
              <a
                href="#features"
                className="inline-flex items-center px-5 py-3 rounded-lg bg-white text-rose-700 font-semibold border border-rose-200 hover:bg-rose-50 transition-colors"
              >
                Ver funciones
              </a>
              <div className="flex items-center gap-2 ml-2">
                <span
                  aria-label="Disponible en Android"
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-white border border-rose-200 text-gray-700"
                  title="Android"
                >
                  <BrandImage baseName="android" className="h-4 w-4 object-contain" />
                  <span className="hidden sm:inline">Android</span>
                </span>
                <span
                  aria-label="Disponible en Web"
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-white border border-rose-200 text-gray-700"
                  title="Web"
                >
                  <span className="text-base">🌐</span>
                  <span className="hidden sm:inline">Web</span>
                </span>
                <span
                  aria-label="iOS próximamente"
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-white border border-rose-200 text-gray-700"
                  title="iOS (Próximamente)"
                >
                  <BrandImage baseName="apple" className="h-4 w-4 object-contain" />
                  <span className="hidden sm:inline">iOS · Próximamente</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="flex flex-col gap-10">
            {[
              { title: 'Agenda por profesionales', desc: 'Cuadrante semanal y vista por recursos para encontrar huecos al instante.', image: 'tablet persona' },
              { title: 'Recordatorios por email', desc: 'Reduce ausencias con avisos automáticos 24h y 2h antes de la cita.', image: 'recordatorios', tightImage: true, spanCols: 1 },
              { title: 'Reservas desde enlace', desc: 'Tus clientes pueden acceder a través de tu enlace único, escaneando un código QR o descargándose la app para reservar al instante.', image: 'qr', tightImage: true, spanCols: 1 },
              { title: 'Gestión de clientes', desc: 'Historial, notas y seguimiento para mejorar la atención.', image: 'usuarios', tightImage: true, spanCols: 1 },
              { title: 'Gestión de trabajadores', desc: 'Organiza a tu equipo con cuadrantes por turnos y consulta el histórico de actividad y citas por empleado.', images: ['trabajadores', 'cuadrante trabajadores'], tightImage: true, spanCols: 1 },
              { title: 'Control de usuarios y reservas', desc: 'Configura la aprobación de nuevos usuarios y el límite de citas por cliente.', image: 'limite', tightImage: true, spanCols: 1 },
              { title: 'Listo para móvil y tablet', desc: 'Interfaz optimizada y modo tablet para tu cuadrante diario.', image: 'tablet', tightImage: true, bigImage: true, spanCols: 2 },
              { title: 'Gestión de horarios por trabajador', desc: 'Define y ajusta horarios por empleado de forma rápida y visual.', image: 'cuadrante', tightImage: true, spanCols: 1 },
              { title: 'Interfaz intuitiva y fácil', desc: 'Experiencia clara para clientes y equipo, con recordatorios integrados de reservas.', images: ['clientes', 'login'], tightImage: true, spanCols: 1 },
            ].map((f, i) => {
              if (f.imageOnly) {
                return (
                  <div key={i} className="rounded-xl border border-rose-100 bg-rose-50 p-6 shadow-sm hover:shadow-md transition-shadow flex items-center justify-center">
                    <BrandImage
                      baseName={f.image}
                      className="w-full max-w-sm h-56 sm:h-64 lg:h-72 object-contain"
                    />
                  </div>
                );
              }

              const rowReverse = i % 2 === 1 ? 'md:flex-row-reverse' : '';
              return (
                <div
                  key={i}
                  className={`rounded-xl border border-rose-100 bg-rose-50 p-6 shadow-sm hover:shadow-md transition-shadow md:flex md:items-center md:gap-8 ${rowReverse}`}
                >
                  <div className="md:w-5/12 w-full flex items-center justify-center mb-4 md:mb-0">
                    {Array.isArray(f.images) && f.images.length > 0 ? (
                      <div className={`grid ${f.images.length > 2 ? 'grid-cols-3' : 'grid-cols-2'} gap-3 w-full`}>
                        {f.images.map((name, idx) => (
                          <BrandImage
                            key={idx}
                            baseName={name}
                            className="w-full h-36 sm:h-40 object-contain rounded-lg"
                          />
                        ))}
                      </div>
                    ) : f.image ? (
                      <BrandImage
                        baseName={f.image}
                        className={f.tightImage
                          ? (f.bigImage
                            ? 'w-full h-64 sm:h-72 lg:h-80 object-contain rounded-lg'
                            : 'w-full h-56 sm:h-60 lg:h-64 object-contain rounded-lg')
                          : 'w-full h-56 sm:h-64 lg:h-72 object-contain rounded-lg border border-rose-100 shadow-sm p-3'}
                      />
                    ) : null}
                  </div>
                  <div className="md:w-7/12 w-full">
                    <h3 className="text-lg font-semibold text-gray-900">{f.title}</h3>
                    <p className="mt-2 text-gray-600">{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ventajas */}
      <section className="bg-rose-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'Rápida de usar', desc: 'Diseñada para minimizar clics y tiempos de espera.' },
              { title: 'Segura', desc: 'Autenticación y datos alojados en infraestructura de confianza.' },
              { title: 'Lista para crecer', desc: 'Añade profesionales, servicios y sedes sin complicaciones.' },
            ].map((b, i) => (
              <div key={i} className="rounded-xl bg-white border border-rose-100 p-6">
                <h4 className="text-base font-semibold text-gray-900">{b.title}</h4>
                <p className="mt-2 text-gray-600 text-sm">{b.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/login"
              className="inline-flex items-center px-6 py-3 rounded-lg bg-rose-600 text-white font-semibold hover:bg-rose-700 transition-colors"
            >
              Empezar ahora
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-sm text-gray-600 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <LogoImage className="h-6 w-auto" />
            <span>© {new Date().getFullYear()} Reserberry</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-rose-700">Privacidad</Link>
            <Link to="/terms" className="hover:text-rose-700">Términos</Link>
            <Link to="/legal" className="hover:text-rose-700">Aviso legal</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;


